import { useEffect, useMemo, useState } from "react";
import { Box, HStack, Button } from "@chakra-ui/react";
import CDS from "design-system";
import { FiPlus } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, horizontalListSortingStrategy, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type {
  AutoAddSide, DensityMode, GridJourney, GridTask, GridTaskTemplate, GridUser, SortMode, TaskGridPreferences, TemplateSortMode,
} from "../types";
import { matchesFilter, dayIndicator, type QuickFilter } from "../helpers";
import { deriveCellState } from "../helpers/cellState";
import { sortJourneys } from "../helpers/sort";
import { sortTemplates } from "../helpers/templateSort";
import { useNow } from "../hooks";
import AddItemPopover from "./AddItemPopover";
import ColumnHeader from "./ColumnHeader";
import RowGroup from "./RowGroup";

interface Props {
  journeys: GridJourney[];
  templates: GridTaskTemplate[];
  tasks: GridTask[];
  assignees: GridUser[];
  preferences: TaskGridPreferences;
  quickFilter: QuickFilter;
  roleFilter: string;
  currentUserId: number | null;
  newHirePlacement: AutoAddSide;
  density: DensityMode;
  sort: SortMode;
  onSortChange: (next: SortMode) => void;
  templateSort: TemplateSortMode;
  onTemplateSortChange: (next: TemplateSortMode) => void;
  onHideJourney: (id: number) => void;
  onHideTemplate: (id: number) => void;
  onRestoreJourney: (id: number) => void;
  onRestoreTemplate: (id: number) => void;
  onReorderJourneys: (orderedIds: string[]) => void;
  onReorderTemplates: (orderedIds: string[]) => void;
}

// Row index and standalone Assignee columns dropped — assignee/role tag now
// renders inline next to the task descriptor to save horizontal space.
const HIRE_COL_COMFORTABLE = "140px";
const HIRE_COL_COMPACT = "100px";

// User-draggable TASK column. Width persists per browser. Below the
// FULL_ROLE_TAG_THRESHOLD, the role pill collapses to its avatar circle
// (hover reveals the role name) so the title and timing line stay readable
// in narrow columns.
const TASK_COL_MIN = 220;
const TASK_COL_MAX = 480;
const TASK_COL_DEFAULT = 300;
const TASK_COL_KEY = "peopleTeamTaskGrid.taskColWidth";
const FULL_ROLE_TAG_THRESHOLD = 260;

const ROW_PREFIX = "row:";
const COL_PREFIX = "col:";

export default function TaskGrid({
  journeys, templates, tasks, assignees, preferences, quickFilter, roleFilter, currentUserId,
  newHirePlacement,
  density,
  sort,
  onSortChange,
  templateSort,
  onTemplateSortChange,
  onHideJourney, onHideTemplate, onRestoreJourney, onRestoreTemplate,
  onReorderJourneys, onReorderTemplates,
}: Props) {
  const today = useNow();
  const [activeId, setActiveId] = useState<string | null>(null);
  const { width: taskColWidth, startResize } = useTaskColumnWidth();
  const effectiveTaskColWidth = taskColWidth;
  const showFullRoleTag = effectiveTaskColWidth >= FULL_ROLE_TAG_THRESHOLD;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // UserBlueprint serializes id as a prefix-id string while our custom task_cell
  // hash sends assignee_id as a raw integer. Normalize both to strings so the
  // lookup works regardless of which side changes shape.
  const assigneeIndex = useMemo(() => {
    const map = new Map<string, GridUser>();
    assignees.forEach((a) => map.set(String(a.id), a));
    return map;
  }, [assignees]);

  // IDs cross a type boundary: prefix-id blueprints serialize as strings while
  // our custom hashes and the preference arrays use numbers. Stringify in every
  // Set/Map/indexOf comparison or lookups silently miss and changes appear to
  // "revert" (they actually never applied to the filter).
  // Stable signature of the visible journey set. Used to keep the sort frozen
  // while only task state changes — completing a cell shouldn't reshuffle the
  // hire whose box you just clicked.
  const journeySetSignature = useMemo(
    () => journeys.map((j) => String(j.id)).sort().join("|"),
    [journeys],
  );
  const customOrderSignature = useMemo(
    () => preferences.journeyOrder.map(String).join("|"),
    [preferences.journeyOrder],
  );
  const hiddenSignature = useMemo(
    () => preferences.hiddenJourneyIds.map(String).sort().join("|"),
    [preferences.hiddenJourneyIds],
  );

  const visibleJourneysFromPrefs = useMemo(() => {
    const hidden = new Set(preferences.hiddenJourneyIds.map(String));
    // Custom order = journey_order (with auto-add placement for unsorted hires).
    // Computed regardless of the active sort so flipping back to Custom restores
    // the manual arrangement rather than losing it.
    const customOrdered = applyOrder(journeys, preferences.journeyOrder, (j) => j.id, newHirePlacement)
      .filter((j) => !hidden.has(String(j.id)));
    const surviving = journeys.filter((j) => !hidden.has(String(j.id)));
    return sortJourneys(surviving, tasks, sort, customOrdered, today);
    // Intentionally NOT depending on `tasks` — re-sorting on every task mutation
    // would yank the column the user just clicked. We re-sort only when the
    // journey set, custom order, sort mode, or auto-add side actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeySetSignature, customOrderSignature, hiddenSignature, newHirePlacement, sort]);

  const visibleTemplatesFromPrefs = useMemo(() => {
    // templateOrder is the source of truth for visibility — a template renders
    // only if the user explicitly added it (via the Add task popover, "Show
    // all templates", or drag-to-reorder). No implicit fallback to "has tasks":
    // a template with zero tasks still renders if it's in the list, and a
    // template with tasks but never added doesn't render until the user opts in.
    const hidden = new Set(preferences.hiddenTemplateIds.map(String));
    const inOrder = new Set(preferences.templateOrder.map(String));
    const customOrdered = applyOrder(templates, preferences.templateOrder, (t) => t.id).filter((t) => {
      const key = String(t.id);
      if (hidden.has(key)) return false;
      return inOrder.has(key);
    });
    return sortTemplates(customOrdered, tasks, templateSort, customOrdered);
    // Intentionally NOT depending on `tasks` — completing a task shouldn't
    // jump rows around. The set of visible templates only changes when the
    // template list, hidden set, custom order, or sort mode shifts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, preferences.hiddenTemplateIds, preferences.templateOrder, templateSort]);

  // Filters prune rows + columns (no longer dim cells). A template is visible
  // only if it has at least one matching task on any journey; same for journeys.
  const { visibleTemplates, visibleJourneys } = useMemo(() => {
    if (quickFilter === "all" && roleFilter === "all") {
      return { visibleTemplates: visibleTemplatesFromPrefs, visibleJourneys: visibleJourneysFromPrefs };
    }
    const matchingTemplateIds = new Set<string>();
    const matchingJourneyIds = new Set<string>();
    tasks.forEach((task) => {
      const passesQuick = matchesFilter(task, quickFilter, currentUserId, today);
      const passesRole = roleFilter === "all" || task.assigneeRole === roleFilter;
      if (passesQuick && passesRole) {
        matchingTemplateIds.add(String(task.templateId));
        matchingJourneyIds.add(String(task.newHireJourneyId));
      }
    });
    return {
      visibleTemplates: visibleTemplatesFromPrefs.filter((t) => matchingTemplateIds.has(String(t.id))),
      visibleJourneys: visibleJourneysFromPrefs.filter((j) => matchingJourneyIds.has(String(j.id))),
    };
  }, [visibleTemplatesFromPrefs, visibleJourneysFromPrefs, quickFilter, roleFilter, currentUserId, today, tasks]);

  const cellIndex = useMemo(() => {
    const map = new Map<string, GridTask>();
    tasks.forEach((task) => {
      map.set(`${task.newHireJourneyId}:${task.templateId}`, task);
    });
    return map;
  }, [tasks]);

  const templateRole = useMemo(() => {
    const map = new Map<string, string | null>();
    templates.forEach((t) => {
      map.set(String(t.id), t.assigneeRole ?? null);
    });
    return map;
  }, [templates]);

  const templateAssigneeUser = useMemo(() => {
    const map = new Map<string, GridUser>();
    templates.forEach((t) => {
      if (t.assigneeUser) map.set(String(t.id), t.assigneeUser);
    });
    return map;
  }, [templates]);

  const templateDescriptors = useMemo(() => {
    const map = new Map<string, { start: string | null; due: string | null }>();
    templates.forEach((t) => {
      map.set(String(t.id), {
        start: t.startAtDescriptor ?? null,
        due: t.dueAtDescriptor ?? null,
      });
    });
    return map;
  }, [templates]);

  const journeyStats = useMemo(() => {
    const map = new Map<string, { overdue: number; done: number; total: number }>();
    visibleJourneys.forEach((j) => map.set(String(j.id), { overdue: 0, done: 0, total: 0 }));
    tasks.forEach((task) => {
      const stat = map.get(String(task.newHireJourneyId));
      if (!stat) return;
      stat.total += 1;
      const state = deriveCellState(task, today);
      if (state.kind === "done") stat.done += 1;
      if (state.kind === "overdue") stat.overdue += 1;
    });
    return map;
  }, [tasks, visibleJourneys, today]);

  const journeyItems = useMemo(() => {
    // Same persisted-state logic as templates: Add popover keys off prefs, not
    // off the active quick/role filter.
    const visibleKeys = new Set(visibleJourneysFromPrefs.map((j) => String(j.id)));
    return journeys.map((j) => ({
      id: j.id,
      name: j.newHire?.fullName ?? "Unknown",
      subtitle: [j.newHire?.title, dayIndicator(j.startDate, today).label].filter((s) => s && s !== "—").join(" · "),
      isHidden: !visibleKeys.has(String(j.id)),
    }));
  }, [journeys, visibleJourneysFromPrefs, today]);

  const templateItems = useMemo(() => {
    // "Hidden" for the Add popover is relative to the PERSISTED view (hidden
    // or no-task + unpinned) — not the temporary quick filter. We don't want
    // a temporary "Overdue" filter to make every non-overdue template look
    // like it needs adding back.
    const visibleKeys = new Set(visibleTemplatesFromPrefs.map((t) => String(t.id)));
    return templates.map((t) => ({
      id: t.id,
      name: t.name,
      isHidden: !visibleKeys.has(String(t.id)),
    }));
  }, [templates, visibleTemplatesFromPrefs]);

  const rowSortableIds = useMemo(() => visibleTemplates.map((t) => `${ROW_PREFIX}${t.id}`), [visibleTemplates]);
  const colSortableIds = useMemo(() => visibleJourneys.map((j) => `${COL_PREFIX}${j.id}`), [visibleJourneys]);

  const handleDragStart = (event: DragStartEvent) => setActiveId(String(event.active.id));

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeKey = String(active.id);
    const overKey = String(over.id);

    if (activeKey.startsWith(ROW_PREFIX) && overKey.startsWith(ROW_PREFIX)) {
      // Compare as strings — visibleTemplates ids are prefix-id strings while
      // active.id is the raw "row:<id>" token, so Number() coercion would miss.
      const currentIds = visibleTemplates.map((t) => String(t.id));
      const oldIdx = currentIds.indexOf(activeKey.slice(ROW_PREFIX.length));
      const newIdx = currentIds.indexOf(overKey.slice(ROW_PREFIX.length));
      if (oldIdx === -1 || newIdx === -1) return;
      onReorderTemplates(arrayMove(currentIds, oldIdx, newIdx));
      if (templateSort !== "custom") onTemplateSortChange("custom");
    } else if (activeKey.startsWith(COL_PREFIX) && overKey.startsWith(COL_PREFIX)) {
      const currentIds = visibleJourneys.map((j) => String(j.id));
      const oldIdx = currentIds.indexOf(activeKey.slice(COL_PREFIX.length));
      const newIdx = currentIds.indexOf(overKey.slice(COL_PREFIX.length));
      if (oldIdx === -1 || newIdx === -1) return;
      onReorderJourneys(arrayMove(currentIds, oldIdx, newIdx));
      // Snap to Custom — the manual arrangement is what the user just expressed,
      // so respect it until they pick an automatic sort again.
      if (sort !== "custom") onSortChange("custom");
    }
  };

  // Keep rendering even with 0 visible hires — the row + "+ New hire" button
  // lets the user restore. Only no-tasks is a true dead-end.
  if (visibleTemplates.length === 0) {
    return (
      <Box p={8} textAlign="center" color="gray.500" border="1px solid" borderColor="gray.100" borderRadius="lg" bg="white">
        <CDS.Text12>Nothing matches the current filters.</CDS.Text12>
      </Box>
    );
  }

  // Build the grid template explicitly — `repeat(0, ...)` is invalid CSS and
  // can cause some engines to insert auto tracks, which would stretch the grid
  // across the full page width when no hires are visible.
  const hireTracks = visibleJourneys.length > 0
    ? ` repeat(${visibleJourneys.length}, ${density === "compact" ? HIRE_COL_COMPACT : HIRE_COL_COMFORTABLE})`
    : "";
  const gridTemplateColumns = `${effectiveTaskColWidth}px${hireTracks} min-content`;

  // Match as strings — t.id/j.id come back as prefix-id strings from blueprints,
  // so Number() coercion would never equal them and the DragOverlay would render
  // null even while a drag is in progress.
  const activeRowTemplate = activeId?.startsWith(ROW_PREFIX)
    ? visibleTemplates.find((t) => String(t.id) === activeId.slice(ROW_PREFIX.length))
    : undefined;
  const activeColJourney = activeId?.startsWith(COL_PREFIX)
    ? visibleJourneys.find((j) => String(j.id) === activeId.slice(COL_PREFIX.length))
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box
        overflow="auto"
        bg="bg.primary"
        border="1px solid"
        borderColor="gray.100"
        borderRadius="lg"
        h="calc(100vh - 200px)"
        maxW="100%"
        w="fit-content"
        boxShadow="sm"
      >
        <Box display="grid" gridTemplateColumns={gridTemplateColumns} w="fit-content">
          <HeaderCell sticky="topLeft" zIndex={3}>
            <ColumnResizeHandle onMouseDown={startResize} />
          </HeaderCell>

          <SortableContext items={colSortableIds} strategy={horizontalListSortingStrategy}>
            {visibleJourneys.map((journey) => {
              const stats = journeyStats.get(String(journey.id)) ?? { overdue: 0, done: 0, total: 0 };
              return (
                <ColumnHeader
                  key={journey.id}
                  journey={journey}
                  stats={stats}
                  today={today}
                  density={density}
                  onHide={onHideJourney}
                />
              );
            })}
          </SortableContext>

          <HeaderCell sticky="top" zIndex={2}>
            <AddItemPopover
              title="Add new hire"
              placeholder="Search new hires..."
              emptyLabel="No additional new hires to show"
              items={journeyItems}
              onAdd={onRestoreJourney}
              trigger={(
                <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiPlus />}>
                  New hire
                </Button>
              )}
            />
          </HeaderCell>

          <SortableContext items={rowSortableIds} strategy={verticalListSortingStrategy}>
            {visibleTemplates.map((template) => (
              <RowGroup
                key={template.id}
                template={template}
                journeys={visibleJourneys}
                cellIndex={cellIndex}
                assigneeIndex={assigneeIndex}
                role={templateRole.get(String(template.id)) ?? null}
                assigneeUser={templateAssigneeUser.get(String(template.id)) ?? null}
                descriptors={templateDescriptors.get(String(template.id)) ?? { start: null, due: null }}
                showFullRoleTag={showFullRoleTag}
                density={density}
                onHideTemplate={onHideTemplate}
                today={today}
              />
            ))}
          </SortableContext>

          <Box gridColumn={`1 / span ${1 + visibleJourneys.length + 1}`} px={3} py={3} borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            {/* Sticky-left so the button stays visible when the grid scrolls
                horizontally — without this, "Add task" disappears off the left
                edge as soon as you scroll right to see more hires. */}
            <Box position="sticky" left={3} display="inline-block">
              <AddItemPopover
                title="Add task"
                placeholder="Search tasks..."
                emptyLabel="No additional tasks to show"
                items={templateItems}
                onAdd={onRestoreTemplate}
                trigger={(
                  <Button size="sm" variant="ghost" colorScheme="blue" leftIcon={<FiPlus />}>
                    Add task
                  </Button>
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* dropAnimation={null} skips the default "fly back to source rect" tween
          so the overlay disappears the moment you release — by then the
          optimistic prefs update has already rendered the new order. */}
      <DragOverlay dropAnimation={null}>
        {activeRowTemplate && (
          <Box bg="white" border="1px solid" borderColor="gray.300" boxShadow="lg" borderRadius="md" px={3} py={2}>
            <HStack spacing={2}>
              <Box as={MdDragIndicator} color="gray.400" boxSize={4} />
              <CDS.Text12 fontWeight="medium">{activeRowTemplate.name}</CDS.Text12>
            </HStack>
          </Box>
        )}
        {activeColJourney && (
          <Box bg="white" border="1px solid" borderColor="gray.300" boxShadow="lg" borderRadius="md" px={3} py={2}>
            <CDS.Text12 fontWeight="semibold">
              {activeColJourney.newHire?.fullName ?? "Unknown"}
            </CDS.Text12>
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
}

interface HeaderCellProps {
  children: React.ReactNode;
  sticky?: "top" | "topLeft";
  zIndex?: number;
  left?: string;
}

function HeaderCell({ children, sticky, zIndex = 2, left }: HeaderCellProps) {
  return (
    <Box
      position="sticky"
      top={0}
      left={sticky === "topLeft" ? 0 : left}
      zIndex={zIndex}
      bg="bg.primary"
      borderBottom="1px solid"
      borderColor="gray.100"
      px={2}
      py={1}
      minH="44px"
      display="flex"
      alignItems="center"
    >
      {children}
    </Box>
  );
}

// Drag handle on the right edge of the TASK column header. The 6px hit area is
// centered on the column boundary so it's easy to grab without overlapping the
// label inside the cell.
function ColumnResizeHandle({ onMouseDown }: { onMouseDown: (clientX: number) => void }) {
  return (
    <Box
      position="absolute"
      top={0}
      right="-3px"
      h="100%"
      w="6px"
      cursor="col-resize"
      zIndex={4}
      _hover={{ bg: "blue.300" }}
      _active={{ bg: "blue.400" }}
      onMouseDown={(event) => {
        event.preventDefault();
        onMouseDown(event.clientX);
      }}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize task column"
    />
  );
}

function clampWidth(n: number): number {
  return Math.max(TASK_COL_MIN, Math.min(TASK_COL_MAX, Math.round(n)));
}

function useTaskColumnWidth() {
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return TASK_COL_DEFAULT;
    const stored = Number(window.localStorage.getItem(TASK_COL_KEY));
    if (Number.isFinite(stored) && stored >= TASK_COL_MIN && stored <= TASK_COL_MAX) return stored;
    return TASK_COL_DEFAULT;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TASK_COL_KEY, String(width));
  }, [width]);

  // Capture-the-mouse pattern: register move/up on window so the drag survives
  // even when the cursor leaves the small handle area.
  const startResize = (startClientX: number) => {
    const startWidth = width;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const handleMove = (event: MouseEvent) => {
      setWidth(clampWidth(startWidth + (event.clientX - startClientX)));
    };
    const handleUp = () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  return { width, startResize };
}

function applyOrder<T>(
  items: T[],
  order: number[],
  getId: (item: T) => number,
  placement: AutoAddSide = "right",
): T[] {
  if (order.length === 0) return items;
  // Stringify both sides — prefs come from the server as integers, item ids
  // come from blueprints as prefix-id strings. Without normalizing, byId.get()
  // returns undefined and the saved order silently never applies.
  const byId = new Map(items.map((i) => [String(getId(i)), i]));
  const ordered: T[] = [];
  order.forEach((id) => {
    const key = String(id);
    const item = byId.get(key);
    if (item) {
      ordered.push(item);
      byId.delete(key);
    }
  });
  const unordered = Array.from(byId.values());
  return placement === "left" ? [...unordered, ...ordered] : [...ordered, ...unordered];
}

